import { ManageBulkTicketViewType, useBulkTicketTransferService } from "./service";
import FormContainer from "shared/form";
import { Form } from "formik";
import FormSelectDropdownField from "shared/form/fields/selectDropdown";
import React, { useMemo } from "react";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import { useAuth } from "shared/context/auth";
import { useGetBulkTicketAdminsList } from "./dataloader";
import { AppType } from "shared/utils/enums";
import { useGetBulkTicketAdminsByRegistration } from "apps/console/modules/registration/approval/details/components/bulkTicketManagement/components/transfer/dataloader";
import Icon from "assets/icons";
import Button from "shared/components/button";
import Variant from "shared/enums/Variant";
import { useCopies } from "shared/hooks/useCopies";
import classNames from "classnames";
import ButtonType from "shared/enums/ButtonTypes";
import FormLabel from "shared/form/fields/formLabel";
import sortBy from "lodash/sortBy";
import { BulkTicketTransferProps } from "./types";
import { BulkTicketViewMode } from "./components/passbook";
import { BulkTicketPassBookFormField } from "./components/passbookFormField";
import { ScrollToErrorOnSubmit } from "shared/form/util";
import Title from "shared/components/title";
import { useRegDetail } from "shared/context/regDetail";
import ButtonGroup from "shared/components/button/components/buttonGroup";

export function BulkTicketTransfer(props: BulkTicketTransferProps) {
  const service = useBulkTicketTransferService(props);
  const copies = useCopies();
  return (
    <Modal className="blkTicketTransferModal">
      <ModalHeader
        left={
          <Title
            title={
              service.viewType === ManageBulkTicketViewType.PASS_BOOK
                ? copies.get("summary")
                : copies.get("bulk.ticket.transfer")
            }
            subTitle={
              service.viewType === ManageBulkTicketViewType.TICKET_TRANSFER &&
              props.renderIn === AppType.CONSOLE && (
                <span className="currentOwnerDetails">
                  <b>{copies.get("bulk.ticket.header.owner.details")}:</b> {props.currentOwner}{" "}
                  <span className="bulletDot" /> {props.currentOwnerOrgName}
                </span>
              )
            }
          />
        }
        onClose={props.closeHandler}
        right={
          service.viewType === ManageBulkTicketViewType.PASS_BOOK && (
            <React.Fragment>
              <Button
                className="bulkTicketTransferBtn"
                onClick={() => service.setViewType(ManageBulkTicketViewType.TICKET_TRANSFER)}
                label={copies.get("bulk.ticket.transfer.button")}
                isOutline
                variant={Variant.LINK}
                icon={<Icon.TransferTicket />}
              />
            </React.Fragment>
          )
        }
      />
      <ModalBody>
        {service.viewType === ManageBulkTicketViewType.PASS_BOOK && <BulkTicketViewMode {...props} />}
        {service.viewType === ManageBulkTicketViewType.TICKET_TRANSFER && (
          <FormContainer
            initialValues={service.initialValues}
            enableReinitialize={true}
            validationSchema={service.validationSchema}
            onSubmit={service.handleBulkTicketTransfer}>
            {formikProps => {
              return (
                <Form data-testid="form">
                  {props.renderIn === AppType.MYEXPERIENCE && <BulkTicketAdminsListForMyExperience {...props} />}
                  {props.renderIn === AppType.CONSOLE && <BulkTicketAdminsListForConsole {...props} />}
                  <FormLabel label={copies.get("bulk.ticket.transfer.choose.ticket.purchase")} />
                  <div className="blkTicketTransferModal_listing">
                    <BulkTicketPassBookFormField {...props} />
                  </div>
                  <ButtonGroup
                    inlineFormButtons={props.renderIn === AppType.CONSOLE}
                    fullWidth={props.renderIn === AppType.MYEXPERIENCE}
                    className={classNames("blkTicketTransferModal_footer")}>
                    <ScrollToErrorOnSubmit />
                    <Button label={copies.get("save")} type={ButtonType.SUBMIT} />
                    <Button
                      isOutline
                      label={copies.get("cancel")}
                      onClick={() => {
                        service.setViewType(ManageBulkTicketViewType.PASS_BOOK);
                      }}
                    />
                  </ButtonGroup>
                </Form>
              );
            }}
          </FormContainer>
        )}
      </ModalBody>
    </Modal>
  );
}

function BulkTicketAdminsListForMyExperience(props: BulkTicketTransferProps) {
  const { userDetail } = useAuth();
  const { helpdeskEmail } = useRegDetail();
  const copies = useCopies();
  const registrationParticipantId = useMemo(() => userDetail?.id, [userDetail?.id]);
  const { data: bulkTicketAdmins } = useGetBulkTicketAdminsList(registrationParticipantId);
  const bulkTicketAdminOptions = useMemo(() => {
    if (!bulkTicketAdmins) {
      return [];
    }
    return sortBy(bulkTicketAdmins, "firstName").map(ticketAdmin => ({
      label: `${ticketAdmin.firstName} ${ticketAdmin.lastName}`,
      value: ticketAdmin.id
    }));
  }, [bulkTicketAdmins]);
  return (
    <FormSelectDropdownField
      required
      options={bulkTicketAdminOptions}
      name={"toId"}
      noOptionsMessage={() => (
        <span className="btaTransfer--noOptionsMessage">
          {copies.get("single.bulk.ticket.from.org.transfer.not.available", { 0: helpdeskEmail })}
        </span>
      )}
      label={copies.get("bulk.ticket.select.participant.to.transfer.to")}
      additionalLabel={copies.get("bulk.ticket.select.participant.to.transfer.to.additional.info.my.experience")}
      fullWidth
      isClearable={false}
    />
  );
}

function BulkTicketAdminsListForConsole(props: BulkTicketTransferProps) {
  const { data: bulkTicketAdmins } = useGetBulkTicketAdminsByRegistration();
  const copies = useCopies();
  const bulkTicketAdminOptions = useMemo(() => {
    if (!bulkTicketAdmins) {
      return [];
    }
    return sortBy(bulkTicketAdmins, "firstName")
      .filter(item => item.id !== props.registrationParticipantId)
      .map(admin => ({
        label: `${admin.firstName} ${admin.lastName} ${admin.organizationName ? `| ${admin.organizationName}` : ""}`,
        value: admin.id
      }));
  }, [bulkTicketAdmins, props.registrationParticipantId]);

  return (
    <FormSelectDropdownField
      required
      isSearchable
      options={bulkTicketAdminOptions}
      name={"toId"}
      label={copies.get("bulk.ticket.select.participant.to.transfer.to")}
      additionalLabel={copies.get("bulk.ticket.select.participant.to.transfer.to.additional.info")}
      fullWidth
      isClearable={false}
    />
  );
}
